import { Link } from 'react-router-dom';
import { useTripBuilder } from '../TripBuilderContext';
import DestImage from '../components/DestImage';
import septemberContext from '../septemberContext';
import PackingList from '../components/PackingList';
import Countdown from '../components/Countdown';
import RouteRibbon from '../components/RouteRibbon';
import ItineraryView from '../components/ItineraryView';
import PreDepartureTimeline from '../components/PreDepartureTimeline';
import TravelDayPlanner from '../components/TravelDayPlanner';
import StopLogisticsCard from '../components/StopLogisticsCard';
import ShareTrip from '../components/ShareTrip';
import CalendarExport from '../components/CalendarExport';

export default function TripSummary() {
  const tb = useTripBuilder();
  const ctx = tb.destination ? septemberContext[tb.destination.city] : null;

  if (!tb.destination) {
    return (
      <div className="max-w-3xl mx-auto text-center py-20">
        <h1 className="text-2xl font-bold mb-2">No trip planned yet</h1>
        <p className="text-brand-ink/70 mb-6">Pick a destination to get started.</p>
        <Link to="/" className="px-6 py-3 bg-teal text-navy font-bold rounded-xl hover:bg-teal-dark transition">
          Start Planning
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-20 space-y-8">
      {/* Hero */}
      <div className="relative rounded-2xl overflow-hidden h-64 sm:h-80">
        <DestImage city={tb.destination.city} />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="text-xs text-teal font-bold uppercase tracking-wider mb-1">Your Trip</div>
          <h1 className="text-4xl font-black">{tb.destination.city}</h1>
          <div className="text-brand-ink/80 mt-1">{tb.destination.country}</div>
          {tb.startDate && tb.endDate && (
            <div className="text-brand-ink/70 text-sm mt-2">
              {new Date(tb.startDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' })}
              {' — '}
              {new Date(tb.endDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
              {' '}&middot; {tb.nights} nights
            </div>
          )}
        </div>
      </div>

      {/* Countdown */}
      {tb.startDate && <Countdown targetDate={tb.startDate} city={tb.destination.city} />}

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Points Used" value={tb.totalPoints.toLocaleString()} sub="Amex + Chase" color="text-teal" />
        <StatCard label="Cash Cost" value={`$${tb.totalCash.toLocaleString()}`} sub="Taxes + activities + food" color="text-gold" />
        <StatCard label="Points Left" value={(tb.amexRemaining + tb.chaseRemaining).toLocaleString()} sub="Still available" color="text-brand-ink/80" />
        <StatCard label="Items Planned" value={tb.itemCount} sub="Flights, hotel, etc." color="text-teal" />
      </div>

      {/* September weather */}
      {ctx && (
        <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-brand-ink/65 uppercase font-bold">September in {tb.destination.city}</div>
            <div className="text-sm text-brand-ink/80 mt-0.5">{ctx.bestFor}</div>
            <div className="text-xs text-brand-ink/65 mt-1">{ctx.weatherVibe} &middot; {ctx.temp} &middot; Water: {ctx.water}</div>
          </div>
          <div className={`text-xs px-2 py-1 rounded-full font-bold ${
            ctx.crowdLevel === 'Very Low' || ctx.crowdLevel === 'Low' ? 'bg-teal/15 text-teal' : 'bg-gold/15 text-gold'
          }`}>{ctx.crowdLevel} crowds</div>
        </div>
      )}

      {/* Route ribbon — the trip shape at a glance */}
      <RouteRibbon />

      {/* Share + Calendar export at the top so they're always reachable */}
      <div className="flex flex-wrap gap-2">
        <ShareTrip />
        <CalendarExport />
      </div>

      {/* Pre-departure timeline — what to do and when */}
      <PreDepartureTimeline />

      {/* Per-stop logistics — country context, transit, bag storage, concierge prep */}
      {tb.stops.length > 0 && (
        <div>
          <h3 className="text-lg font-bold mb-3">Per-stop logistics</h3>
          <div className="space-y-3">
            {tb.stops.map((s, i) => (
              <StopLogisticsCard key={i} city={s.destination.city} stopIndex={i} />
            ))}
          </div>
        </div>
      )}

      {/* Travel-day plans — only meaningful when multi-city */}
      {tb.isMultiCity && tb.stops.length > 1 && (
        <div>
          <h3 className="text-lg font-bold mb-3">Travel days (door-to-door scripts)</h3>
          <div className="space-y-3">
            {tb.stops.slice(0, -1).map((stop, i) => {
              // Compute the date of this transition: arrival at stop i+1
              const startDate = new Date(tb.startDate + 'T12:00:00');
              const nightsBefore = tb.stops.slice(0, i + 1).reduce((s, x) => s + (x.nights || 0), 0);
              const transitionDate = new Date(startDate);
              transitionDate.setDate(transitionDate.getDate() + nightsBefore);
              return (
                <TravelDayPlanner
                  key={i}
                  fromStop={stop}
                  toStop={tb.stops[i + 1]}
                  transitionDate={transitionDate}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Day-by-day itinerary — the END-TO-END plan with every booking link */}
      <div>
        <h3 className="text-lg font-bold mb-3">Day-by-day itinerary</h3>
        <ItineraryView />
      </div>

      {/* Cost breakdown — multi-city aware */}
      <Section title="Full cost breakdown" icon="💰">
        <div className="space-y-2">
          {tb.isMultiCity ? (
            <>
              {tb.legs.map((leg) => leg.flight ? (
                <CostLine
                  key={'leg' + leg.index}
                  label={`${leg.kind === 'outbound' ? 'Outbound' : leg.kind === 'return' ? 'Return' : `Leg ${leg.index + 1}`} · ${leg.origin} → ${leg.destinationCodes[0]} via ${leg.flight.airline_program}`}
                  points={tb.scaleForTravelers(leg.flight.total_points_two_travelers)}
                  cash={tb.scaleForTravelers(leg.flight.total_estimated_taxes)}
                  source={leg.flight.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'}
                />
              ) : null)}
              {tb.stops.map((s, i) => s.hotel ? (
                <CostLine
                  key={'hotel' + i}
                  label={`Hotel in ${s.destination.city} · ${s.hotel.hotel_name} · ${s.nights}n`}
                  points={s.hotel.total_points}
                  source={s.hotel.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'}
                />
              ) : null)}
              {tb.stops.flatMap((s, i) =>
                (s.activities || []).map(a => (
                  <CostLine key={'act' + i + '-' + a.id} label={`${s.destination.city}: ${a.name}`} cash={tb.scaleForTravelers(a.price_for_two)} />
                ))
              )}
              {tb.stops.flatMap((s, i) =>
                (s.restaurants || []).map(r => (
                  <CostLine key={'rest' + i + '-' + r.id} label={`${s.destination.city}: ${r.name}`} cash={tb.scaleForTravelers(r.avg_meal_for_two)} />
                ))
              )}
            </>
          ) : (
            <>
              {tb.flight && (
                <CostLine
                  label={`Flights · ${tb.flight.airline_program} (${tb.userConfig.travelers} ${tb.userConfig.travelers === 1 ? 'traveler' : 'travelers'}, ${tb.userConfig.cabinClass})`}
                  points={tb.flightPoints}
                  cash={tb.flightTaxes}
                  source={tb.flight.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'}
                />
              )}
              {tb.hotel && (
                <CostLine
                  label={`Hotel · ${tb.hotel.hotel_name} (${tb.hotel.nights}n)`}
                  points={tb.hotel.total_points}
                  source={tb.hotel.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'}
                />
              )}
              {tb.activities.map(a => (
                <CostLine key={a.id} label={a.name} cash={tb.scaleForTravelers(a.price_for_two)} />
              ))}
              {tb.restaurants.map(r => (
                <CostLine key={r.id} label={r.name} cash={tb.scaleForTravelers(r.avg_meal_for_two)} />
              ))}
            </>
          )}
          <div className="border-t border-brand-ink/10 pt-2 mt-3 space-y-1">
            <div className="flex justify-between text-sm font-bold">
              <span>Total Points</span>
              <span className="text-teal">{tb.totalPoints.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm font-bold">
              <span>Total Cash (taxes + activities + food)</span>
              <span className="text-gold">${tb.totalCash.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-brand-ink/65">Points remaining after trip</span>
              <span className="text-brand-ink/75">{(tb.amexRemaining + tb.chaseRemaining).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Packing List */}
      <PackingList destination={tb.destination} activities={tb.activities} flight={tb.flight} />

      {/* Footer */}
      <div className="text-center py-8 border-t border-brand-ink/10">
        <div className="text-sm text-teal/70 font-bold">PointsPilot</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-3 text-center">
      <div className={`text-xl font-black ${color}`}>{value}</div>
      <div className="text-[10px] text-brand-ink/65 uppercase font-bold mt-0.5">{label}</div>
      <div className="text-[10px] text-brand-ink/45">{sub}</div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-5">
      <div className="flex items-center gap-2 mb-3">
        <span>{icon}</span>
        <h3 className="font-bold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function CostLine({ label, points, cash, source }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-brand-ink/75">{label}</span>
      <div className="text-right">
        {points > 0 && <span className="text-brand-ink/80">{points.toLocaleString()} pts</span>}
        {points > 0 && cash > 0 && <span className="text-brand-ink/45 mx-1">+</span>}
        {cash > 0 && <span className="text-gold">${cash.toLocaleString()}</span>}
        {source && <span className="text-[10px] text-brand-ink/50 ml-2">({source})</span>}
      </div>
    </div>
  );
}
